import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 27.37.U — Public landing-page chatbot widget ("Kora").
 *
 * Floating pill in the bottom-right of a landing page. When opened,
 * it starts a session with the anonymous `/public/chat/start` endpoint
 * and streams the conversation with Claude Sonnet 5. Once Claude
 * marks `ready_for_form=true`, the widget bubbles the collected
 * fields up via `onQualified(fields)` so the parent can pre-fill the
 * lead form and gently scroll to it.
 *
 * Props:
 *   slug              — landing page slug (required)
 *   language          — 'en' | 'es' — initial language (defaults 'en')
 *   accentColor       — brand color for the header
 *   onQualified       — function({first_name, phone, email, area_of_interest, state, is_licensed_agent}, session_id)
 */
export default function PublicChatWidget({ slug, language = 'en', accentColor = '#0f2a5e', onQualified }) {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null); // {session_id, language}
  const [messages, setMessages] = useState([]);
  const [collected, setCollected] = useState({});
  const [ready, setReady] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [handedOff, setHandedOff] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const scrollRef = useRef(null);

  const t = (en, es) => (session?.language === 'es' ? es : en);

  // Resume from sessionStorage on mount so a page refresh doesn't
  // wipe the conversation.
  useEffect(() => {
    if (!slug) return;
    try {
      const stored = sessionStorage.getItem(`kora:${slug}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSession(parsed.session);
        setMessages(parsed.messages || []);
        setCollected(parsed.collected || {});
        setReady(parsed.ready || false);
        setQuickReplies(parsed.quickReplies || []);
      }
    } catch { /* ignore parse errors */ }
  }, [slug]);

  // Persist so a refresh doesn't lose progress.
  useEffect(() => {
    if (!slug || !session) return;
    try {
      sessionStorage.setItem(`kora:${slug}`, JSON.stringify({ session, messages, collected, ready, quickReplies }));
    } catch { /* storage may be blocked in incognito */ }
  }, [slug, session, messages, collected, ready, quickReplies]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const start = useCallback(async () => {
    if (session || starting) return;
    setStarting(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/chat/start`, { slug, language });
      setSession({ session_id: data.session_id, language: data.language });
      setMessages(data.messages || [{ role: 'assistant', text: data.greeting }]);
      setQuickReplies(data.quick_replies || []);
    } catch (e) {
      setMessages([{ role: 'assistant', text: t(
        "Sorry — the chat is unavailable right now. Please use the form below.",
        "Disculpa — el chat no está disponible. Por favor usa el formulario abajo.",
      ) }]);
    } finally {
      setStarting(false);
    }
  }, [slug, language, session, starting]);

  const openWidget = () => {
    setOpen(true);
    try { sessionStorage.setItem(`kora:auto:${slug}`, '1'); } catch { /* ignore */ }
    if (!session) start();
  };

  const closeWidget = () => {
    setOpen(false);
    try { sessionStorage.setItem(`kora:auto:${slug}`, '1'); } catch { /* ignore */ }
  };

  // Proactive open — Kora invites the visitor instead of waiting in the
  // corner. Fires once per session on the earliest of: 12s dwell, 35%
  // scroll depth, or exit-intent. Respects a manual dismiss.
  useEffect(() => {
    if (!slug || open) return undefined;
    try { if (sessionStorage.getItem(`kora:auto:${slug}`) === '1') return undefined; } catch { /* ignore */ }
    let done = false;
    const trigger = () => {
      if (done) return;
      done = true;
      cleanup();
      openWidget();
    };
    const onScroll = () => {
      const h = document.body.scrollHeight - window.innerHeight;
      if (h > 0 && (window.scrollY / h) > 0.35) trigger();
    };
    const onMouseOut = (e) => { if (e.clientY <= 0 && !e.relatedTarget) trigger(); };
    const timer = setTimeout(trigger, 12000);
    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseout', onMouseOut);
    return cleanup;
  }, [slug, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (overrideText) => {
    const text = (overrideText != null ? overrideText : input).trim();
    if (!text || !session || sending) return;
    setInput('');
    setQuickReplies([]);
    setSending(true);
    setMessages((m) => [...m, { role: 'user', text }]);
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/chat/${session.session_id}/message`,
        { text },
      );
      setMessages((m) => [...m, { role: 'assistant', text: data.reply }]);
      setCollected(data.collected_fields || {});
      setQuickReplies(data.quick_replies || []);
      setSession((s) => ({ ...s, language: data.language || s.language }));
      if (data.ready_for_form && !handedOff) {
        setHandedOff(true);
        // Attempt to CREATE the lead immediately — no form re-entry.
        // If the backend rejects (missing pieces), fall back to the
        // old behaviour of pre-filling the form.
        try {
          const { data: created } = await axios.post(
            `${BASE}/api/public/chat/${session.session_id}/create-lead`,
          );
          setReady(true);
          if (typeof onQualified === 'function') {
            // Signal parent: lead created — hide the form.
            onQualified(data.collected_fields || {}, session.session_id, {
              leadId: created.lead_id,
              autoCreated: true,
            });
          }
        } catch {
          setReady(true);
          if (typeof onQualified === 'function') {
            // Fallback: pre-fill only.
            onQualified(data.collected_fields || {}, session.session_id, {
              autoCreated: false,
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: t(
        "I couldn't send that — try again in a moment.",
        "No pude enviar eso — intenta de nuevo en un momento.",
      )}]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) {
    return (
      <button
        onClick={openWidget}
        aria-label="Open chat"
        data-testid="chatbot-open-btn"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-shadow text-white font-medium text-sm"
        style={{ backgroundColor: accentColor }}
      >
        <MessageCircle size={18} />
        <span>{t('Chat with Kora', 'Chatea con Kora')}</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      style={{ height: '520px', maxHeight: '80vh' }}
      data-testid="chatbot-widget"
    >
      <header
        className="flex items-center justify-between px-4 py-3 text-white"
        style={{ backgroundColor: accentColor }}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles size={16} />
          </div>
          <div>
            <div className="font-semibold text-sm">Kora</div>
            <div className="text-[10.5px] opacity-90">
              {t('KR Enterprise · Virtual concierge', 'KR Enterprise · Concierge virtual')}
            </div>
          </div>
        </div>
        <button onClick={closeWidget} aria-label="Minimize" className="text-white/80 hover:text-white" data-testid="chatbot-close-btn">
          <ChevronDown size={18} />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50" data-testid="chatbot-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-slate-800 text-white rounded-br-sm'
                  : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-slate-500 italic">
              {t('Kora is typing…', 'Kora está escribiendo…')}
            </div>
          </div>
        )}
        {ready && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-800 flex items-start gap-2">
            <span>✓</span>
            <span>
              {t(
                "You're all set. A licensed agent will call you shortly.",
                'Listo. Un agente con licencia lo llamará en breve.',
              )}
            </span>
          </div>
        )}
      </div>

      {quickReplies.length > 0 && !ready && !sending && (
        <div className="px-3 pb-2 pt-1 bg-slate-50 flex flex-wrap gap-1.5" data-testid="chatbot-quick-replies">
          {quickReplies.map((qr, i) => (
            <button
              key={i}
              onClick={() => send(qr)}
              disabled={sending || !session}
              className="text-xs font-medium rounded-full px-3 py-1.5 border bg-white hover:bg-slate-100 transition-colors disabled:opacity-40"
              style={{ borderColor: accentColor, color: accentColor }}
              data-testid={`chatbot-quick-reply-${i}`}
            >
              {qr}
            </button>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 p-2 bg-white flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={t('Type a message…', 'Escribe un mensaje…')}
          className="flex-1 resize-none rounded border border-slate-200 focus:border-slate-400 outline-none px-2 py-1.5 text-sm max-h-24"
          disabled={sending || starting}
          data-testid="chatbot-input"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim() || !session}
          className="flex-shrink-0 rounded-full w-8 h-8 flex items-center justify-center text-white disabled:opacity-40"
          style={{ backgroundColor: accentColor }}
          data-testid="chatbot-send-btn"
          aria-label="Send"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

export function attachLeadToChatSession(sessionId, leadId) {
  if (!sessionId || !leadId) return Promise.resolve();
  return axios.post(`${BASE}/api/public/chat/${sessionId}/attach-lead`, { lead_id: leadId })
    .catch(() => null);
}
